import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { DailyStatsDto } from '@buspro/web/apis';

@Component({
  selector: 'buspro-admin-daily-stats-card',
  standalone: true,
  imports: [CommonModule, CurrencyPipe],
  template: `
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <!-- Total Bookings -->
      <div class="stat-card bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 font-medium">Today's Bookings</p>
            <p class="text-3xl font-bold text-gray-900 mt-2">
              {{ stats?.totalBookingsToday || 0 }}
            </p>
            <p class="text-xs text-gray-500 mt-1">Total bookings today</p>
          </div>
          <div class="p-3 bg-blue-100 rounded-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-8 h-8 text-blue-600"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776"
              />
            </svg>
          </div>
        </div>
      </div>

      <!-- Revenue -->
      <div class="stat-card bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 font-medium">Revenue</p>
            <p class="text-3xl font-bold text-gray-900 mt-2">
              {{ (stats?.totalRevenue || 0) | currency:'UGX':'symbol':'1.0-0' }}
            </p>
            <p class="text-xs text-gray-500 mt-1">Total revenue today</p>
          </div>
          <div class="p-3 bg-green-100 rounded-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-8 h-8 text-green-600"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>
      </div>

      <!-- Average ETA -->
      <div class="stat-card bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 font-medium">Average ETA</p>
            <p class="text-3xl font-bold text-gray-900 mt-2">
              {{ stats?.averageETA || 0 }} <span class="text-lg">min</span>
            </p>
            <p class="text-xs text-gray-500 mt-1">Average arrival time</p>
          </div>
          <div class="p-3 bg-purple-100 rounded-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-8 h-8 text-purple-600"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>
      </div>

      <!-- Peak Hours -->
      <div class="stat-card bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <p class="text-sm text-gray-600 font-medium">Peak Hours</p>
            <div class="mt-2">
              @if (stats && stats.peakHours && stats.peakHours.length > 0) {
                <div class="flex flex-wrap gap-2">
                  @for (hour of stats.peakHours; track hour) {
                    <span class="badge badge-warning badge-sm">{{ hour }}</span>
                  }
                </div>
              } @else {
                <p class="text-lg font-semibold text-gray-400">N/A</p>
              }
            </div>
            <p class="text-xs text-gray-500 mt-1">Busiest times</p>
          </div>
          <div class="p-3 bg-orange-100 rounded-full ml-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-8 h-8 text-orange-600"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class DailyStatsCardComponent {
  @Input() stats: DailyStatsDto | null = null;
}
